"use client"

import { AlertTriangle, ExternalLink, Info, Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { useWhatsappAccountReadiness } from "@/hooks/queries/whatsapp-account-readiness"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

export function WhatsappAccountReadiness({ channelId }: { channelId: string }) {
  const t = useTranslations("WhatsappTemplates")
  const query = useWhatsappAccountReadiness(channelId)
  if (!channelId) return null
  if (query.isPending) return <p role="status" className="flex items-center gap-2 text-base"><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /><span>{t("readinessChecking")}</span></p>
  const data = query.data
  const paymentIssue = data?.status === "payment_issue"
  const paymentUnknown = !data || data.status === "unknown"
  const nameReady = data?.nameStatus === "APPROVED"
  const verified = data?.businessVerification === "verified"
  return <section className="space-y-4 rounded-lg border bg-background p-4 text-base">
    <h3 className="font-semibold"><span>{t("readinessTitle")}</span></h3>
    <TooltipProvider>
      <dl className="grid gap-4 sm:grid-cols-3">
        <Status label={t("paymentLabel")} value={paymentLabel(data?.status, t)} hint={t("paymentHint")} ready={!paymentIssue && !paymentUnknown} />
        <Status label={t("businessLabel")} value={businessLabel(data?.businessVerification, t)} hint={t("businessHint")} ready={verified} />
        <Status label={t("displayNameLabel")} value={nameLabel(data?.nameStatus, t)} hint={t("readinessNameHint")} ready={nameReady} />
      </dl>
    </TooltipProvider>
    {data?.displayName ? <p className="break-words"><span>{t("currentName", { name: data.displayName })}</span></p> : null}
    {data?.phoneNotices?.map((notice, index) => <p key={`${index}:${notice}`} className="rounded-md bg-amber-50 p-3 text-amber-950 dark:bg-amber-950 dark:text-amber-100"><span>{notice}</span></p>)}
    {paymentIssue || paymentUnknown ? <div role="alert" className="space-y-2 rounded-lg border-2 border-amber-400 bg-amber-50 p-4 text-amber-950 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-100">
      <p className="flex items-start gap-2 font-semibold"><AlertTriangle aria-hidden="true" className="mt-1 h-5 w-5 shrink-0" /><span key={paymentIssue ? "issue" : "unknown"}>{paymentIssue ? t("paymentIssueTitle") : t("paymentUnknownTitle")}</span></p>
      <p><span key={paymentIssue ? "issue" : "unknown"}>{paymentIssue ? t("paymentIssueBody") : t("paymentUnknownBody")}</span></p>
    </div> : null}
    {!nameReady ? <p><span>{t("nameGuidance")}</span></p> : null}
    <div className="flex flex-wrap gap-3">
      <ExternalAction href={data?.billingUrl ?? "https://business.facebook.com/latest/billing_hub/accounts/"} label={t("paymentOpenBilling")} />
      <ExternalAction href={data?.verificationUrl ?? "https://business.facebook.com/settings/security/"} label={t("verifyBusiness")} />
      <ExternalAction href={data?.phoneUrl ?? "https://business.facebook.com/wa/manage/phone-numbers/"} label={t("manageDisplayName")} />
      <Button type="button" variant="outline" className="text-base" disabled={query.isFetching} onClick={() => void query.refetch()}>{query.isFetching ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : null}<span>{t("paymentRecheck")}</span></Button>
    </div>
  </section>
}

function Status({ label, value, hint, ready }: { label: string; value: string; hint: string; ready: boolean }) {
  return <div className="space-y-2"><dt className="flex items-center gap-2"><span>{label}</span><Tooltip><TooltipTrigger asChild><button type="button" aria-label={label} className="rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Info aria-hidden="true" className="h-4 w-4" /></button></TooltipTrigger><TooltipContent className="max-w-xs text-sm"><span>{hint}</span></TooltipContent></Tooltip></dt><dd><Badge variant="outline" className={ready ? "border-green-300 bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200" : "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100"}><span key={value}>{value}</span></Badge></dd></div>
}

function ExternalAction({ href, label }: { href: string; label: string }) {
  return <Button asChild variant="outline" className="h-auto min-h-10 whitespace-normal text-base"><a href={href} target="_blank" rel="noopener noreferrer"><span>{label}</span><ExternalLink aria-hidden="true" className="h-4 w-4 shrink-0" /></a></Button>
}

type Translator = ReturnType<typeof useTranslations<"WhatsappTemplates">>
function businessLabel(status: string | null | undefined, t: Translator): string {
  switch (status) {
    case "verified": return t("verifiedStatus")
    case null: case undefined: return t("unknownStatus")
    default: return t("notVerifiedStatus")
  }
}
function paymentLabel(status: string | undefined, t: Translator): string {
  switch (status) {
    case "payment_issue": return t("actionRequiredStatus")
    case "not_reported": return t("noPaymentIssueStatus")
    default: return t("unknownStatus")
  }
}
function nameLabel(status: string | null | undefined, t: Translator): string {
  switch (status) {
    case "APPROVED": return t("approved")
    case "AVAILABLE_WITHOUT_REVIEW": return t("availableWithoutReview")
    case "PENDING_REVIEW": return t("pending")
    case "DECLINED": return t("rejected")
    case "NONE": return t("nameNotSubmitted")
    case "EXPIRED": return t("nameExpired")
    default: return t("unknownStatus")
  }
}
